import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";

const getMillis = (ts) => (ts?.toMillis ? ts.toMillis() : 0);

/**
 * Load Leopardy games where the current user is host or player.
 */
export function useLeopardyGames(user) {
  const [hostGames, setHostGames] = useState([]);
  const [playerGames, setPlayerGames] = useState([]);
  const [hostLoaded, setHostLoaded] = useState(false);
  const [playerLoaded, setPlayerLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      setHostGames([]);
      setPlayerGames([]);
      setHostLoaded(false);
      setPlayerLoaded(false);
      setError(null);
      return;
    }

    const col = collection(db, "games_leopardy");
    setHostLoaded(false);
    setPlayerLoaded(false);

    const unsubHost = onSnapshot(
      query(col, where("hostUid", "==", user.uid)),
      (snap) => {
        setHostGames(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setHostLoaded(true);
        setError(null);
      },
      (err) => {
        console.error("[LeopardyGames] host", err);
        setError(err.message || "Failed to load Leopardy games.");
        setHostLoaded(true);
      }
    );

    const unsubPlayer = onSnapshot(
      query(col, where("playerIds", "array-contains", user.uid)),
      (snap) => {
        setPlayerGames(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setPlayerLoaded(true);
        setError(null);
      },
      (err) => {
        console.error("[LeopardyGames] player", err);
        setError(err.message || "Failed to load Leopardy games.");
        setPlayerLoaded(true);
      }
    );

    return () => {
      unsubHost();
      unsubPlayer();
    };
  }, [user]);

  const games = useMemo(() => {
    const byId = new Map();
    hostGames.forEach((g) => byId.set(g.id, g));
    playerGames.forEach((g) => byId.set(g.id, g));
    return Array.from(byId.values()).sort((a, b) => {
      const aTs = getMillis(a.updatedAt) || getMillis(a.createdAt);
      const bTs = getMillis(b.updatedAt) || getMillis(b.createdAt);
      return bTs - aTs;
    });
  }, [hostGames, playerGames]);

  const loading = !!user && (!(hostLoaded && playerLoaded));

  return {
    hostGames,
    playerGames,
    games,
    loading,
    error,
  };
}
